import { sfx } from './sfx.js';
import { SOULS, HEART_PATH } from './pixels.js';
import { loadProducts, createCart, money, sized, shopUrl, onShopify } from './store.js';

const $ = (s, el = document) => el.querySelector(s);
const $$ = (s, el = document) => [...el.querySelectorAll(s)];
const html = document.documentElement;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Optional settings from the Shopify theme (<script id="aorta-config">). The static
// site has none, so every value below falls back to the original behaviour.
const CFG = (() => { try { const el = document.getElementById('aorta-config'); return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; } })();
const SND = CFG.sound || {};
const T = (key, fallback) => (CFG.strings && CFG.strings[key] != null ? CFG.strings[key] : fallback);
const pageLinks = CFG.productLinks === 'page';
const soundEnabled = SND.enabled !== false;

let gestured = false;
const markGesture = () => { if (!gestured) { gestured = true; sfx.unlock(); } };
window.addEventListener('pointerdown', markGesture, { capture: true });
window.addEventListener('keydown', markGesture, { capture: true });
const play = (name, ...args) => { if (soundEnabled && gestured && !sfx.muted && sfx[name]) sfx[name](...args); };

/* ── small bits ─────────────────────────────────────────────────────────── */

if ($('#year')) $('#year').textContent = new Date().getFullYear();

// forms and policy links point at the store (same-origin once this runs on Shopify)
$$('form[data-shopify-form]').forEach((f) => { f.action = shopUrl('/contact') + (f.id === 'joinForm' ? '#join' : '#contact'); });
$$('[data-shop-path]').forEach((a) => { a.href = shopUrl(a.dataset.shopPath); });

const soundBtn = $('#soundBtn');
const syncSound = () => {
  if (!soundBtn) return;
  soundBtn.setAttribute('aria-pressed', String(!sfx.muted));
  soundBtn.setAttribute('aria-label', sfx.muted ? T('soundOff', 'Sound off') : T('soundOn', 'Sound on'));
};
syncSound();
if (soundBtn) soundBtn.addEventListener('click', () => { sfx.setMuted(!sfx.muted); syncSound(); play('tick'); });

// mobile menu
const menuBtn = $('#menuBtn');
const navLinks = $('#navLinks');
const setMenu = (open) => {
  if (!navLinks || !menuBtn) return;
  navLinks.classList.toggle('is-open', open);
  menuBtn.setAttribute('aria-expanded', String(open));
};
if (menuBtn && navLinks) {
  menuBtn.addEventListener('click', () => setMenu(!navLinks.classList.contains('is-open')));
  navLinks.addEventListener('click', (e) => { if (e.target.closest('a')) setMenu(false); });
}

/* ── typewriter dialog ──────────────────────────────────────────────────── */

function typeLine(el, text, { html: rich = false, speed = CFG.typeSpeed || 34 } = {}) {
  clearInterval(el._typer);
  if (reducedMotion) { el[rich ? 'innerHTML' : 'textContent'] = text; return; }
  const tmp = document.createElement('div');
  tmp.innerHTML = rich ? text : esc(text);
  const full = tmp.textContent;
  let i = 0;
  el.classList.add('typing');
  el.textContent = '';
  el._typer = setInterval(() => {
    i++;
    if (rich) {
      // re-render the rich string clipped to i visible characters
      let left = i;
      const clone = tmp.cloneNode(true);
      const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((n) => { const take = Math.max(0, Math.min(n.data.length, left)); left -= take; n.data = n.data.slice(0, take); });
      el.innerHTML = clone.innerHTML;
    } else {
      el.textContent = full.slice(0, i);
    }
    const ch = full[i - 1];
    if (ch && ch !== ' ' && i % 2 === 0 && SND.blips !== false) play('blip');
    if (i >= full.length) { clearInterval(el._typer); el.classList.remove('typing'); }
  }, speed);
}

/* ── reveal on scroll ───────────────────────────────────────────────────── */

const revealIO = new IntersectionObserver((entries) => {
  entries.forEach((e) => {
    if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
  });
}, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
function armReveals(root = document) {
  $$('.reveal:not(.is-in)', root).forEach((el) => {
    const sib = el.parentElement ? [...el.parentElement.children].filter((c) => c.classList.contains('reveal')) : [];
    const idx = sib.indexOf(el);
    if (idx > 0) el.style.setProperty('--d', Math.min(idx, 6) * 0.08 + 's');
    revealIO.observe(el);
  });
}

/* ── scroll state ───────────────────────────────────────────────────────── */

let scene = null;
const topbar = $('#topbar');
// menu links that point at a section on this page ("#shop", or "/#shop" on the homepage)
const navMap = new Map($$('.nav-links a').map((a) => {
  let u = null;
  try { u = new URL(a.getAttribute('href'), location.href); } catch (e) { return [null, a]; }
  const here = u.pathname === location.pathname && u.hash.length > 1;
  return [here ? decodeURIComponent(u.hash.slice(1)) : null, a];
}).filter(([id]) => id));
const sections = [...navMap.keys()].map((id) => document.getElementById(id));

let scrollQueued = false;
function onScroll() {
  if (scrollQueued) return;
  scrollQueued = true;
  requestAnimationFrame(() => {
    scrollQueued = false;
    const y = window.scrollY;
    if (scene) scene.setScroll(y);
    if (topbar) topbar.classList.toggle('is-scrolled', y > 30);
    const mid = y + innerHeight * 0.4;
    let active = null;
    sections.forEach((s) => { if (s && docTop(s) <= mid) active = s.id; });
    navMap.forEach((a, id) => a.classList.toggle('is-active', id === active));
  });
}
window.addEventListener('scroll', onScroll, { passive: true });

function docTop(el) { let y = 0; while (el) { y += el.offsetTop; el = el.offsetParent; } return y; }
function docLeft(el) { let x = 0; while (el) { x += el.offsetLeft; el = el.offsetParent; } return x; }

// where the sky turns into water: a section marked data-waterline (Shopify theme),
// else the souls ring (the original layout), else the first underwater section,
// else the footer
function waterlineY() {
  const marked = $('[data-waterline]');
  const ring = $('#soulsRing');
  if (marked) {
    const mode = marked.dataset.waterline;
    if (mode === 'ring' && ring && marked.contains(ring)) return docTop(ring) + ring.offsetHeight * 0.68;
    const top = docTop(marked), h = marked.offsetHeight;
    if (mode === 'top') return top;
    if (mode === 'bottom') return top + h;
    if (mode === 'ring') return top + h * 0.6;
    return top + h * 0.5;
  }
  if (ring) return docTop(ring) + ring.offsetHeight * 0.68;
  const deep = $('main .deep') || $('.foot');
  return deep ? docTop(deep) : document.documentElement.scrollHeight;
}

function pushLayout() {
  if (!scene) return;
  const stage = $('#heroStage');
  scene.setLayout({
    stage: stage && stage.offsetWidth ? { x: docLeft(stage), y: docTop(stage), w: stage.offsetWidth, h: stage.offsetHeight } : null,
    waterY: waterlineY(),
    docH: document.documentElement.scrollHeight,
  });
  scene.setScroll(window.scrollY);
}
new ResizeObserver(() => pushLayout()).observe(document.body);
window.addEventListener('resize', pushLayout);
if (document.fonts) document.fonts.ready.then(pushLayout);

/* ── intro ──────────────────────────────────────────────────────────────── */

// pages without the homepage (policies, account, search on the Shopify theme) have no intro
const intro = $('#intro');
if (!intro && html.classList.contains('has-intro')) html.classList.replace('has-intro', 'no-intro');
let introActive = html.classList.contains('has-intro');
let introDone = !introActive;
const afterIntro = [];
const whenEntered = (fn) => { if (introDone) fn(); else afterIntro.push(fn); };

const heroLine = $('#heroLine');
const heroText = heroLine ? heroLine.textContent.trim() : '';

function revealSite() {
  html.classList.add('entered');
  armReveals();
  if (heroLine) setTimeout(() => typeLine(heroLine, heroText), 700);
}

function finishIntro() {
  introDone = true;
  intro.hidden = true;
  intro.classList.remove('is-leaving');
  try { sessionStorage.setItem('aorta:entered', '1'); } catch (e) {}
  afterIntro.splice(0).forEach((fn) => fn());
  onScroll();
}

// used when WebGL is unavailable: the CSS key does the job
function fallbackEnter() {
  if (introDone) return;
  play('enter');
  intro.classList.add('is-leaving');
  revealSite();
  setTimeout(finishIntro, 900);
}

function startEnter() {
  if (introDone) return;
  if (scene && scene.introPlaying) scene.enter(); else fallbackEnter();
}

window.addEventListener('keydown', (e) => {
  if (introDone || e.key !== 'Enter' || e.repeat) return;
  if (document.activeElement && document.activeElement.id === 'enterBtn') return;
  e.preventDefault();
  if (scene && scene.introPlaying) scene.pressKey(); else fallbackEnter();
});
window.addEventListener('keyup', (e) => {
  if (introDone || e.key !== 'Enter') return;
  if (scene && scene.introPlaying) scene.releaseKey(true);
});
if (intro) {
  $('#enterBtn').addEventListener('click', startEnter);
  $('#cssKey').addEventListener('click', fallbackEnter);

  if ('ontouchstart' in window) {
    const t = $('.intro-hint-text');
    if (t && t.dataset.touch) t.textContent = t.dataset.touch;
  }

  if ($('#replayIntro')) $('#replayIntro').addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    introDone = false;
    html.classList.remove('entered');
    html.classList.remove('no-intro');
    html.classList.add('has-intro');
    intro.hidden = false;
    intro.classList.remove('is-leaving');
    if (scene) scene.replayIntro();
    if (heroLine) heroLine.textContent = '';
  });
}

if (!introActive) revealSite();

/* ── WebGL scene (loaded lazily so the page never depends on it) ────────── */

(async () => {
  if (!$('#scene')) return; // pages without the canvas use the CSS sky
  let mod = null;
  try {
    mod = await import('./scene.js');
  } catch (err) {
    console.warn('3D scene unavailable', err);
  }
  scene = mod && mod.createScene({
    canvas: $('#scene'),
    introActive,
    reducedMotion,
    sfx: {
      keyDown: () => play('keyDown'),
      enter: () => play('enter'),
      pop: (r) => play('pop', r),
      soul: (i) => play('soul', i),
    },
    onIntroGone() {
      intro.classList.add('is-leaving');
      revealSite();
    },
    onEnterDone: finishIntro,
  });
  if (!scene) {
    html.classList.add('no-webgl');
    html.classList.add('scene-ready');
    return;
  }
  pushLayout();
  scene.onReady(() => html.classList.add('scene-ready'));
  if (/[?&]debug\b/.test(location.search)) window.__aorta = scene;
})();

// clicks on empty background pop bubbles and spin hearts
const UI = 'a,button,input,textarea,select,label,summary,dialog,.glass,.pcard,.tile,.dialog,.cart,.topbar,.foot-grid,h1,h2,h3,p,img';
const pokeOn = !CFG.scene || CFG.scene.poke !== false;
window.addEventListener('pointerdown', (e) => {
  if (!scene || !introDone || e.button > 0 || !pokeOn) return;
  if (e.target.closest(UI)) return;
  scene.poke(e.clientX, e.clientY);
});
let hoverQueued = false;
window.addEventListener('pointermove', (e) => {
  if (!scene || !introDone || hoverQueued || e.pointerType !== 'mouse' || !pokeOn) return;
  hoverQueued = true;
  requestAnimationFrame(() => {
    hoverQueued = false;
    const free = !e.target.closest(UI);
    document.body.style.cursor = free && scene.hoverTarget(e.clientX, e.clientY) ? 'pointer' : '';
  });
}, { passive: true });

/* ── souls ring ─────────────────────────────────────────────────────────── */

const ORDER = ['patience', 'perseverance', 'bravery', 'justice', 'kindness', 'integrity', 'determination'];
const shade = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  let r = n >> 16, g = (n >> 8) & 255, b = n & 255;
  const t = amt < 0 ? 0 : 255, p = Math.abs(amt);
  r = Math.round((t - r) * p + r); g = Math.round((t - g) * p + g); b = Math.round((t - b) * p + b);
  return `rgb(${r},${g},${b})`;
};

function soulSVG(s) {
  const id = s.id;
  return `<svg viewBox="-1 -1 16 14" aria-hidden="true">
    <defs>
      <linearGradient id="sg-${id}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${shade(s.color, 0.45)}"/>
        <stop offset=".48" stop-color="${s.color}"/>
        <stop offset="1" stop-color="${shade(s.color, -0.42)}"/>
      </linearGradient>
      <clipPath id="sc-${id}"><path d="${HEART_PATH}"/></clipPath>
    </defs>
    <path d="${HEART_PATH}" fill="url(#sg-${id})" stroke="${shade(s.color, -0.6)}" stroke-width=".75" paint-order="stroke" stroke-linejoin="miter"/>
    <g clip-path="url(#sc-${id})">
      <ellipse cx="7" cy="1.2" rx="8.4" ry="4.4" fill="url(#soulGloss)"/>
      <ellipse cx="7" cy="12.4" rx="5" ry="3.2" fill="${shade(s.color, 0.55)}" opacity=".45"/>
    </g>
    <path d="M2 1h2v1H2zM1 2h1v2H1z" fill="#fff" opacity=".95"/>
  </svg>`;
}

function buildRing() {
  const ring = $('#ring');
  if (!ring || ring.dataset.built) return;
  ring.dataset.built = '1';
  const angles = [-120, -80, -40, 0, 40, 80, 120];
  const R = 37;
  const chain = `<svg class="chain" viewBox="0 0 100 100" aria-hidden="true">
    <defs>
      <linearGradient id="chainGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#ffffff"/><stop offset=".35" stop-color="#b9c7d3"/><stop offset=".6" stop-color="#f4f8fb"/><stop offset="1" stop-color="#8496a6"/>
      </linearGradient>
      <linearGradient id="soulGloss" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#fff" stop-opacity=".95"/><stop offset="1" stop-color="#fff" stop-opacity="0"/>
      </linearGradient>
    </defs>
    <circle class="chain-links" cx="50" cy="50" r="${R}"/>
    <circle class="chain-links alt" cx="50" cy="50" r="${R}"/>
    <g transform="rotate(158 50 50)"><ellipse cx="50" cy="${50 - R}" rx="3.4" ry="2.2" fill="url(#chainGrad)" stroke="#7d8c99" stroke-width=".5"/></g>
  </svg>`;
  ring.innerHTML = chain + ORDER.map((id, i) => {
    const s = SOULS.find((x) => x.id === id);
    const a = (angles[i] * Math.PI) / 180;
    const x = 50 + Math.sin(a) * R, y = 50 - Math.cos(a) * R;
    return `<button class="soul" type="button" data-soul="${id}" style="left:${x}%;top:${y}%;--rot:${angles[i] * 0.25}deg;--c:${s.color}" aria-label="${esc(s.name)}">${soulSVG(s)}</button>`;
  }).join('');

  const line = $('#soulLine');
  ring.addEventListener('click', (e) => {
    const b = e.target.closest('.soul');
    if (!b) return;
    const s = SOULS.find((x) => x.id === b.dataset.soul);
    $$('.soul', ring).forEach((x) => x.classList.toggle('is-on', x === b));
    b.classList.remove('is-hop'); void b.offsetWidth; b.classList.add('is-hop');
    line.parentElement.style.setProperty('--c', s.color);
    typeLine(line, `<b>${esc(s.name)}.</b>${s.line ? ' ' + esc(s.line) : ''}`, { html: true, speed: Math.round((CFG.typeSpeed || 34) * 40 / 34) });
    play('soul', SOULS.indexOf(s));
  });
}
buildRing();

/* ── products ───────────────────────────────────────────────────────────── */

let products = [];
const byHandle = new Map();
let cart = null;

const shipCopy = (p) => p.preorder
  ? `<strong>Preorder.</strong> Allow ${p.shipWindow} for production, packing, shipping and handling. If you order this with a premade piece, everything ships together once this is ready.`
  : `<strong>Ready to ship.</strong> Allow ${p.shipWindow} for packing, shipping and handling.`;

function statusChip(p) {
  return p.preorder
    ? `<span class="status preorder"><i></i>${esc(T('statusPreorder', 'Preorder'))} · ${p.shipWindow}</span>`
    : `<span class="status ready"><i></i>${esc(T('statusReady', 'Ready'))} · ${p.shipWindow}</span>`;
}
const buyLabel = (p) => esc(p.available ? (p.preorder ? T('preorder', 'Preorder') : T('add', 'Add to cart')) : T('soldOut', 'Sold out'));

function renderProducts() {
  const grid = $('#productGrid');
  // the Shopify theme renders its cards in Liquid; only prices need nothing here
  if (!grid || grid.hasAttribute('data-server-rendered')) { pushLayout(); return; }
  grid.innerHTML = products.map((p) => `
    <article class="pcard glass glare reveal" data-handle="${esc(p.handle)}" data-soul="${p.tint}">
      <button class="pcard-media" type="button" data-open="${esc(p.handle)}" aria-label="View ${esc(p.name)}">
        <img src="${sized(p.images[0] && p.images[0].src, 900)}" alt="${esc(p.name)}" loading="lazy" width="900" height="900">
      </button>
      <div class="pcard-tags">${statusChip(p)}<span class="price-pill">${money(p.price, true)}</span></div>
      <div class="pcard-body">
        <h3 class="pcard-name"><button type="button" data-open="${esc(p.handle)}">${esc(p.name)}</button></h3>
        <p class="pcard-blurb">${esc(p.blurb)}</p>
        <div class="pcard-actions">
          <button class="btn gel" type="button" data-add="${esc(p.handle)}" ${p.available ? '' : 'disabled'}><span>${buyLabel(p)}</span></button>
          <button class="btn glass-btn" type="button" data-open="${esc(p.handle)}"><span>${esc(T('details', 'Details'))}</span></button>
        </div>
      </div>
    </article>`).join('');
  armReveals(grid);
  $$('[data-price]').forEach((el) => { const p = byHandle.get(el.dataset.price); if (p) el.textContent = money(p.price, true); });
  pushLayout();
}

// 3D tilt + glare on product cards
document.addEventListener('pointermove', (e) => {
  const card = e.target.closest && e.target.closest('.pcard');
  if (!card || e.pointerType !== 'mouse' || reducedMotion || CFG.tilt === false) return;
  const r = card.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
  card.style.setProperty('--mx', x * 100 + '%');
  card.style.setProperty('--my', y * 100 + '%');
  card.style.transform = `rotateX(${(0.5 - y) * 7}deg) rotateY(${(x - 0.5) * 9}deg) translateY(-4px)`;
}, { passive: true });
document.addEventListener('pointerout', (e) => {
  const card = e.target.closest && e.target.closest('.pcard');
  if (card && !card.contains(e.relatedTarget)) card.style.transform = '';
});

/* ── product window ─────────────────────────────────────────────────────── */

const win = $('#productWin');
const winBody = $('#winBody');

function openProduct(handle, { push = true } = {}) {
  const p = byHandle.get(handle);
  if (!p || !win) return;
  $('#winTitle').textContent = `${p.name} — AortaNYC`;
  const imgs = p.images;
  winBody.innerHTML = `
    <div class="pw-gallery">
      <div class="pw-main"><img src="${sized(imgs[0] && imgs[0].src, 1200)}" alt="${esc(p.name)}"></div>
      ${imgs.length > 1 ? `<div class="pw-thumbs">${imgs.map((im, i) => `<button class="pw-thumb" type="button" data-i="${i}" aria-current="${i === 0}" aria-label="Photo ${i + 1}"><img src="${sized(im.src, 240)}" alt=""></button>`).join('')}</div>` : ''}
    </div>
    <div class="pw-info">
      <div class="pw-meta">${statusChip(p)}<span class="price-pill">${money(p.price)}</span></div>
      <h2>${esc(p.name)}</h2>
      <p class="pw-ship ${p.preorder ? 'is-pre' : ''}">${shipCopy(p)}</p>
      <div class="pw-buy">
        <div class="qty" role="group" aria-label="Quantity">
          <button type="button" data-step="-1" aria-label="Fewer">−</button>
          <output id="pwQty">1</output>
          <button type="button" data-step="1" aria-label="More">+</button>
        </div>
        <button class="btn gel" type="button" id="pwAdd" ${p.available ? '' : 'disabled'}><span>${buyLabel(p)} · ${money(p.price, true)}</span></button>
      </div>
      <details class="pw-details" open><summary>Details</summary><div class="rte">${p.body}</div></details>
    </div>`;
  let qty = 1;
  winBody.querySelector('.qty').addEventListener('click', (e) => {
    const b = e.target.closest('[data-step]');
    if (!b) return;
    qty = Math.max(1, Math.min(20, qty + Number(b.dataset.step)));
    $('#pwQty').textContent = qty;
    play('tick');
  });
  $('#pwAdd').addEventListener('click', (e) => addToCart(p, qty, e.currentTarget));
  winBody.querySelectorAll('.pw-thumb').forEach((t) => t.addEventListener('click', () => {
    const im = imgs[Number(t.dataset.i)];
    const main = winBody.querySelector('.pw-main img');
    main.src = sized(im.src, 1200);
    winBody.querySelectorAll('.pw-thumb').forEach((x) => x.setAttribute('aria-current', String(x === t)));
  }));
  win.classList.remove('is-closing');
  if (!win.open) win.showModal();
  winBody.scrollTop = 0;
  play('open');
  if (push) history.replaceState(null, '', '#product/' + handle);
}

function closeWin() {
  if (!win || !win.open || win.classList.contains('is-closing')) return;
  win.classList.add('is-closing');
  play('close');
  setTimeout(() => { win.close(); win.classList.remove('is-closing'); }, reducedMotion ? 0 : 230);
  if (location.hash.startsWith('#product/')) history.replaceState(null, '', location.pathname + location.search);
}
if (win) {
  win.addEventListener('cancel', (e) => { e.preventDefault(); closeWin(); });
  win.addEventListener('click', (e) => { if (e.target === win || e.target.closest('[data-close]')) closeWin(); });
}

/* ── cart drawer ────────────────────────────────────────────────────────── */

const cartEl = $('#cart');
const scrim = $('#cartScrim');
const cartBtn = $('#cartBtn');
const badge = $('#cartCount');

function setBadge(n, bump = false) {
  if (!badge || !cartBtn) return;
  badge.hidden = n < 1;
  badge.textContent = n > 99 ? '99+' : n;
  const open = T('openCart', 'Open cart');
  cartBtn.setAttribute('aria-label', n ? `${open}, ${n}` : open);
  if (bump) { badge.classList.remove('bump'); void badge.offsetWidth; badge.classList.add('bump'); }
}

const EMPTY_HEART = `<svg viewBox="-1 -1 16 14"><defs><linearGradient id="eg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#e9f8ff"/><stop offset=".5" stop-color="#9fdcff"/><stop offset="1" stop-color="#3fa9ec"/></linearGradient></defs><path d="${HEART_PATH}" fill="url(#eg)" stroke="#2a8ad0" stroke-width=".6" paint-order="stroke"/><path d="M2 1h2v1H2zM1 2h1v2H1z" fill="#fff"/></svg>`;

function renderCart(c) {
  setBadge(c.count);
  const list = $('#cartItems');
  if (!list) return;
  if (!c.items.length) {
    list.innerHTML = `<div class="cart-empty">${EMPTY_HEART}<strong>${esc(T('cartEmptyTitle', 'Your cart is empty.'))}</strong><span>${esc(T('cartEmptyText', "Despite everything, there's still room."))}</span><a class="btn btn-sm gel" href="${esc(T('cartEmptyLink', '#shop'))}" data-close-cart><span>${esc(T('cartEmptyButton', 'Browse the pieces'))}</span></a></div>`;
  } else {
    list.innerHTML = c.items.map((i) => `
      <div class="citem" data-key="${esc(i.key)}">
        <img src="${sized(i.image, 200)}" alt="">
        <div>
          <h3>${esc(i.name)}</h3>
          <div class="muted">${esc(i.preorder ? T('statusPreorder', 'Preorder') : T('readyShort', 'Ready to ship'))} · ${money(i.price)}</div>
          <div class="qty" role="group" aria-label="Quantity for ${esc(i.name)}">
            <button type="button" data-q="${i.qty - 1}" aria-label="Fewer">−</button>
            <output>${i.qty}</output>
            <button type="button" data-q="${i.qty + 1}" aria-label="More">+</button>
          </div>
        </div>
        <div>
          <div class="citem-price">${money(i.price * i.qty)}</div>
          <button class="citem-remove" type="button" data-q="0">${esc(T('cartRemove', 'Remove'))}</button>
        </div>
      </div>`).join('');
  }
  $('#cartSubtotal').textContent = money(c.subtotal);
  const note = $('#cartNote');
  const mixed = T('cartMixed', 'Heads up: your cart has a preorder and a premade piece, so everything ships together once the preorder is ready.');
  note.hidden = !(c.hasPreorder && c.hasPremade) || !mixed;
  note.textContent = mixed;
  const co = $('#checkoutBtn');
  co.href = cart.checkoutUrl();
  co.setAttribute('aria-disabled', String(!c.items.length));
}

let scrimTimer = 0;
function openCart() {
  // cart button set to open the cart page (Theme settings → Cart), or no drawer here
  if (!cartEl) { location.href = CFG.cartUrl || '/cart'; return; }
  clearTimeout(scrimTimer);
  scrim.hidden = false;
  requestAnimationFrame(() => scrim.classList.add('is-on'));
  cartEl.classList.add('is-open');
  cartEl.removeAttribute('inert');
  cartEl.setAttribute('aria-hidden', 'false');
  $('#cartClose').focus({ preventScroll: true });
  play('open');
  cart && cart.get().then(renderCart);
}
function closeCart() {
  if (!cartEl || !cartEl.classList.contains('is-open')) return;
  scrim.classList.remove('is-on');
  cartEl.classList.remove('is-open');
  cartEl.setAttribute('inert', '');
  cartEl.setAttribute('aria-hidden', 'true');
  scrimTimer = setTimeout(() => { scrim.hidden = true; }, 400);
  cartBtn.focus({ preventScroll: true });
  play('close');
  if (location.hash === '#cart') history.replaceState(null, '', location.pathname + location.search);
}
if (cartBtn && cartBtn.tagName === 'BUTTON') cartBtn.addEventListener('click', openCart);
if ($('#cartClose')) $('#cartClose').addEventListener('click', closeCart);
if (scrim) scrim.addEventListener('click', closeCart);
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeCart(); setMenu(false); } });
if (cartEl) cartEl.addEventListener('click', async (e) => {
  if (e.target.closest('[data-close-cart]')) { closeCart(); return; }
  const b = e.target.closest('[data-q]');
  if (!b || !cart) return;
  const key = b.closest('.citem').dataset.key;
  play('tick');
  renderCart(await cart.setQty(key, Math.max(0, Number(b.dataset.q))));
});

/* ── add to cart: a bubble floats to the cart ───────────────────────────── */

function flyBubble(from) {
  if (reducedMotion || !from || !cartBtn || CFG.bubble === false) return Promise.resolve();
  const a = from.getBoundingClientRect();
  const b = cartBtn.getBoundingClientRect();
  const el = document.createElement('div');
  el.style.cssText = `position:fixed;z-index:90;left:${a.left + a.width / 2 - 18}px;top:${a.top + a.height / 2 - 18}px;width:36px;height:36px;border-radius:50%;pointer-events:none;
    background:radial-gradient(circle at 32% 28%,#fff 0 12%,rgba(255,255,255,.2) 30%,rgba(120,220,255,.15) 60%,rgba(255,255,255,.75) 100%);
    box-shadow:inset 0 0 8px rgba(255,255,255,.9),0 0 18px rgba(120,220,255,.8);`;
  document.body.appendChild(el);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  const anim = el.animate([
    { transform: 'translate(0,0) scale(.4)', opacity: 0 },
    { transform: `translate(${dx * 0.35}px, ${dy * 0.35 - 80}px) scale(1.1)`, opacity: 1, offset: 0.35 },
    { transform: `translate(${dx}px, ${dy}px) scale(.55)`, opacity: 0.9 },
  ], { duration: 750, easing: 'cubic-bezier(.4,0,.2,1)' });
  return anim.finished.then(() => {
    el.animate([{ transform: `translate(${dx}px, ${dy}px) scale(.55)`, opacity: 0.9 }, { transform: `translate(${dx}px, ${dy}px) scale(1.6)`, opacity: 0 }], { duration: 220 }).finished.then(() => el.remove());
  });
}

async function addToCart(p, qty = 1, btn) {
  if (!cart || !p || !p.available) return;
  const label = btn && btn.querySelector('span');
  const old = label && label.innerHTML;
  if (btn) { btn.disabled = true; label.innerHTML = esc(T('adding', 'Adding…')); }
  try {
    const flight = flyBubble(btn);
    const c = await cart.add(p.variantId, qty);
    await flight;
    play('add');
    setBadge(c.count, true);
    renderCart(c);
    toast(p, qty);
  } catch (err) {
    console.error(err);
    toast(null, 0, T('toastError', 'Could not add that right now. Please try again.'));
  } finally {
    if (btn) { btn.disabled = false; label.innerHTML = old; }
  }
}

document.addEventListener('click', (e) => {
  const add = e.target.closest('[data-add]');
  // the Shopify theme's add buttons sit in a real cart form: until the cart is
  // ready (or if this product isn't known), let the form post as normal
  if (add) {
    if (cart && byHandle.has(add.dataset.add)) { e.preventDefault(); addToCart(byHandle.get(add.dataset.add), 1, add); }
    return;
  }
  if (pageLinks) return; // product links go to real product pages
  const open = e.target.closest('[data-open]');
  // before products load, a link with a real href just navigates
  if (open && byHandle.has(open.dataset.open)) { e.preventDefault(); openProduct(open.dataset.open); return; }
  const chip = e.target.closest('[data-product-link]');
  if (chip && byHandle.has(chip.dataset.productLink)) { e.preventDefault(); openProduct(chip.dataset.productLink); }
});

/* ── toasts ─────────────────────────────────────────────────────────────── */

function toast(p, qty, message) {
  const box = $('#toasts');
  const el = document.createElement('div');
  el.className = 'toast';
  el.innerHTML = message
    ? `<span class="orb orb-xs orb-pink"><svg viewBox="0 0 24 24"><path d="M12 7v6M12 16.5v.5"/></svg></span><div>${esc(message)}</div>`
    : `<span class="orb orb-xs orb-lime"><svg viewBox="0 0 24 24"><path d="m6.5 12.5 3.5 3.5 7.5-8"/></svg></span>
       <div>${esc(T('toastAdded', 'Added to cart'))}<small>${qty > 1 ? qty + ' × ' : ''}${esc(p.name)}</small></div>
       <button class="btn btn-sm glass-btn" type="button"><span>${esc(T('toastView', 'View cart'))}</span></button>`;
  const btn = el.querySelector('button');
  if (btn) btn.addEventListener('click', () => { dismiss(); if (win && win.open) closeWin(); openCart(); });
  box.appendChild(el);
  let gone = false;
  function dismiss() {
    if (gone) return;
    gone = true;
    el.classList.add('is-out');
    setTimeout(() => el.remove(), 360);
  }
  setTimeout(dismiss, 4200);
}

/* ── boot ───────────────────────────────────────────────────────────────── */

(async () => {
  try {
    products = await loadProducts();
  } catch (err) {
    console.error(err);
    const grid = $('#productGrid');
    if (grid && !grid.hasAttribute('data-server-rendered')) grid.innerHTML = `<p class="sub" style="text-align:center">${esc(T('shopDown', 'The shop is taking a breather.'))} <a href="${shopUrl('/collections/all')}">Open the catalog</a>.</p>`;
    return;
  }
  products.forEach((p) => byHandle.set(p.handle, p));
  cart = createCart(products);
  renderProducts();
  renderCart(await cart.get());
  // deep links: #product/<handle> opens a window, #cart opens the cart (the Shopify
  // theme's product and cart pages set these), #<section> re-aligns once cards are in
  const m = /^#product\/(.+)$/.exec(location.hash);
  if (m && byHandle.has(decodeURIComponent(m[1])) && !pageLinks) whenEntered(() => setTimeout(() => openProduct(decodeURIComponent(m[1]), { push: false }), 300));
  else if (location.hash === '#cart') whenEntered(() => setTimeout(openCart, 300));
  else if (/^#[\w-]+$/.test(location.hash)) {
    const target = document.getElementById(location.hash.slice(1));
    if (target) whenEntered(() => target.scrollIntoView({ behavior: 'instant', block: 'start' }));
  }
})();

/* ── product page (Shopify theme) ───────────────────────────────────────── */

// The page works without this: the form posts to the cart. With it, photos swap,
// the price follows the chosen variant and adding happens in place.
function initProductPage(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';
  let data = { variants: [] };
  try { data = JSON.parse($('[data-product-json]', root).textContent); } catch (e) {}
  const form = $('form[data-aorta-buy]', root);
  const qtyInput = form && $('input[name="quantity"]', form);
  const mainImg = $('[data-main-media] img', root);
  const labels = data.labels || {};
  const btnShowsPrice = !!(form && $('[data-button-price]', form));

  const showMedia = (src, alt, id) => {
    if (!mainImg || !src) return;
    mainImg.removeAttribute('srcset');
    mainImg.src = src;
    if (alt) mainImg.alt = alt;
    $$('[data-thumb]', root).forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === String(id))));
  };

  root.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-thumb]');
    if (thumb) { showMedia(thumb.dataset.src, thumb.dataset.alt, thumb.dataset.mediaId); play('tick'); return; }
    const step = e.target.closest('[data-step]');
    if (step && qtyInput) {
      qtyInput.value = Math.max(1, Math.min(99, (Number(qtyInput.value) || 1) + Number(step.dataset.step)));
      play('tick');
    }
  });

  const chosenId = () => {
    const sel = $('select[data-variant-input]', root);
    if (sel) return sel.value;
    const radio = $('input[data-variant-input]:checked', root);
    if (radio) return radio.value;
    const hidden = form && $('input[name="id"]', form);
    return hidden ? hidden.value : null;
  };

  const sync = () => {
    const v = data.variants.find((x) => String(x.id) === String(chosenId()));
    if (!v) return;
    const pill = $('[data-price-pill]', root);
    if (pill) pill.innerHTML = (v.compareText ? `<s>${esc(v.compareText)}</s>` : '') + esc(v.priceText);
    const price = $('[data-price]', root);
    if (price) price.innerHTML = `<span>${esc(v.priceText)}</span>` + (v.compareText ? `<s>${esc(v.compareText)}</s>` : '');
    const btn = form && $('[data-add-button]', form);
    if (btn) {
      btn.disabled = !v.available;
      const label = $('[data-add-label]', btn);
      const word = !v.available ? labels.soldOut : (data.preorder ? labels.preorder : labels.add);
      if (label) label.innerHTML = esc(word || '') + (btnShowsPrice ? ` · <b data-button-price>${esc(v.priceShort)}</b>` : '');
    }
    if (v.image) showMedia(v.image, null, v.mediaId);
    const url = new URL(location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState(null, '', url);
  };
  root.addEventListener('change', (e) => { if (e.target.matches('[data-variant-input]')) { sync(); play('tick'); } });

  if (form) form.addEventListener('submit', (e) => {
    if (!cart) return; // the cart isn't ready: post the form normally
    e.preventDefault();
    const id = chosenId();
    const qty = Math.max(1, Number(qtyInput && qtyInput.value) || 1);
    const btn = $('[data-add-button]', form);
    addToCart({ name: data.name || document.title, variantId: id, available: true, preorder: !!data.preorder }, qty, btn);
  });
}
$$('[data-aorta-product]').forEach(initProductPage);

/* ── theme editor: sections re-render without a page load ───────────────── */

document.addEventListener('shopify:section:load', (e) => {
  armReveals(e.target);
  buildRing();
  $$('[data-aorta-product]', e.target).forEach(initProductPage);
  setTimeout(pushLayout, 50);
});
['shopify:section:unload', 'shopify:section:reorder'].forEach((ev) => document.addEventListener(ev, () => setTimeout(pushLayout, 50)));

if (onShopify) html.classList.add('on-shopify');
